/**
 * SyncStatusHeader Component
 *
 * Compact sync status indicator for app headers with dropdown actions.
 *
 * Features:
 * - Responsive design: icon+dot on mobile, icon+label on desktop
 * - Dropdown with status display and context-appropriate actions
 * - Handles all 8 connection states
 * - Safe context access (returns null if outside SyncConnectionProvider)
 * - 44px touch targets for mobile accessibility
 *
 * @example Wizard variant (onboarding)
 * ```tsx
 * <SyncStatusHeader variant="wizard" providerKey="google-drive" />
 * ```
 *
 * @example Main app variant with settings link
 * ```tsx
 * <SyncStatusHeader variant="main" providerKey="google-drive" showSettingsLink />
 * ```
 */

import React, { memo, useContext, useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cloud,
  CloudOff,
  RefreshCw,
  AlertCircle,
  WifiOff,
  Clock,
  ChevronDown,
  Link as LinkIcon,
  Unlink,
  X,
  Settings,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// UI Components
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Button,
} from '@enfomentis/ui';

// Context
import { SyncConnectionContext } from '@/contexts/SyncConnectionContext';

// Utilities
import { getProviderDisplayName } from '@/utils/syncProviderUtils';
import { openOAuthPopup } from '@/utils/oauthPopupUtils';

// Types
import type { SyncStatusHeaderProps } from './SyncStatusHeader.types';
import type { ChipProps } from '@enfomentis/ui';

/**
 * Status configuration for each connection state
 */
interface StatusConfig {
  /** Short label for display */
  label: string;
  /** Color for the status dot */
  dotColor: 'default' | 'success' | 'warning' | 'danger';
  /** HeroUI semantic color for label/chip (used in responsive design) */
  chipColor: NonNullable<ChipProps['color']>;
  /** Icon component */
  Icon: LucideIcon;
  /** Whether icon should animate (spin) */
  iconAnimate: boolean;
}

/**
 * SyncStatusHeader - Compact sync status indicator with dropdown actions
 *
 * Displays connection status in header and provides quick access to
 * connect/disconnect/reconnect actions via dropdown menu.
 */
function SyncStatusHeaderComponent(props: SyncStatusHeaderProps): React.ReactElement | null {
  const {
    variant,
    providerKey,
    showSettingsLink = false,
    className,
    lastSyncAt,
    syncStatus,
    schemaVersionError,
    onSyncNow,
  } = props;

  const navigate = useNavigate();

  // Safe context access - return null if outside SyncConnectionProvider
  const connectionContext = useContext(SyncConnectionContext);
  const state = connectionContext?.state;

  // Local state for popup blocked warning
  const [popupBlocked, setPopupBlocked] = useState(false);

  // Controlled dropdown state - keeps dropdown open during sync
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const syncInProgressRef = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-close dropdown after sync completes (with delay)
  useEffect(() => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    // If sync was in progress and now completed (success or idle after success)
    if (syncInProgressRef.current && (syncStatus === 'success' || syncStatus === 'idle')) {
      // For 'success', wait 2 seconds to show the checkmark, then close
      // For 'idle' (after success transitions), close immediately if still open
      if (syncStatus === 'success') {
        closeTimeoutRef.current = setTimeout(() => {
          setIsDropdownOpen(false);
          syncInProgressRef.current = false;
        }, 2000);
      } else {
        // Already transitioned to idle, close now
        setIsDropdownOpen(false);
        syncInProgressRef.current = false;
      }
    }

    // Track when sync starts
    if (syncStatus === 'syncing') {
      syncInProgressRef.current = true;
    }

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [syncStatus]);

  // Get provider display name
  const providerDisplayName = useMemo(
    () => getProviderDisplayName(providerKey),
    [providerKey]
  );

  // Derive status configuration from connection state
  // Priority: schema version error > sync status > connection state
  const statusConfig = useMemo((): StatusConfig => {
    // Schema version errors take highest priority
    if (schemaVersionError) {
      if (schemaVersionError.code === 'SCHEMA_VERSION_NEWER') {
        // User needs to refresh browser - warning state
        return {
          label: 'Sync Paused',
          dotColor: 'warning',
          chipColor: 'warning',
          Icon: AlertTriangle,
          iconAnimate: false,
        };
      }
      // MISSING, INVALID, MISMATCH - corrupted cloud data - error state
      return {
        label: 'Sync Error',
        dotColor: 'danger',
        chipColor: 'danger',
        Icon: AlertCircle,
        iconAnimate: false,
      };
    }

    if (!state) {
      // Default config when no context (shouldn't render, but type-safe fallback)
      return {
        label: 'Not Connected',
        dotColor: 'default',
        chipColor: 'default',
        Icon: CloudOff,
        iconAnimate: false,
      };
    }

    switch (state.status) {
      case 'idle':
        return {
          label: 'Not Connected',
          dotColor: 'default',
          chipColor: 'default',
          Icon: CloudOff,
          iconAnimate: false,
        };

      case 'connecting':
        return {
          label: 'Connecting',
          dotColor: 'warning',
          chipColor: 'warning',
          Icon: RefreshCw,
          iconAnimate: true,
        };

      case 'remoteConnecting':
        return {
          label: 'Another tab connecting',
          dotColor: 'warning',
          chipColor: 'warning',
          Icon: RefreshCw,
          iconAnimate: true,
        };

      case 'success':
        return {
          label: 'Connected',
          dotColor: 'success',
          chipColor: 'success',
          Icon: Cloud,
          iconAnimate: false,
        };

      case 'needsReauth':
        return {
          label: 'Reconnect Required',
          dotColor: 'danger',
          chipColor: 'danger',
          Icon: AlertCircle,
          iconAnimate: false,
        };

      case 'offline':
        return {
          label: 'Offline',
          dotColor: 'warning',
          chipColor: 'warning',
          Icon: WifiOff,
          iconAnimate: false,
        };

      case 'rateLimited':
        return {
          label: 'Rate Limited',
          dotColor: 'warning',
          chipColor: 'warning',
          Icon: Clock,
          iconAnimate: false,
        };

      case 'error':
        return {
          label: 'Error',
          dotColor: 'danger',
          chipColor: 'danger',
          Icon: AlertCircle,
          iconAnimate: false,
        };

      default: {
        // Exhaustiveness check
        const _exhaustive: never = state;
        console.warn('[SyncStatusHeader] Unknown state:', _exhaustive);
        return {
          label: 'Unknown',
          dotColor: 'default',
          chipColor: 'default',
          Icon: CloudOff,
          iconAnimate: false,
        };
      }
    }
  }, [schemaVersionError, state]);

  /**
   * Handle connect action
   * - For google-drive: Opens OAuth popup synchronously, then calls connect
   * - For local: Calls connect directly (FSA flow)
   */
  const handleConnect = useCallback(() => {
    if (!connectionContext) return;

    // Clear any previous popup blocked state
    setPopupBlocked(false);

    if (providerKey === 'google-drive') {
      // Open OAuth popup synchronously to preserve user gesture
      const popup = openOAuthPopup(providerKey);

      if (!popup) {
        // Popup was blocked
        setPopupBlocked(true);
        return;
      }

      // Pass popup to connect
      connectionContext.connect(providerKey, { oauthPopup: popup });
    } else {
      // Local provider - no popup needed, FSA flow
      connectionContext.connect(providerKey);
    }
  }, [connectionContext, providerKey]);

  /**
   * Handle disconnect action
   */
  const handleDisconnect = useCallback(() => {
    if (!connectionContext) return;
    connectionContext.disconnect();
  }, [connectionContext]);

  /**
   * Handle cancel action (abort in-flight connection)
   */
  const handleCancel = useCallback(() => {
    if (!connectionContext) return;
    connectionContext.abort();
  }, [connectionContext]);

  /**
   * Handle settings navigation
   */
  const handleSettingsClick = useCallback(() => {
    navigate('/settings/data-sync');
  }, [navigate]);

  /**
   * Handle Sync Now action
   * Keeps dropdown open during sync
   */
  const handleSyncNow = useCallback(async () => {
    if (!onSyncNow) return;
    syncInProgressRef.current = true;
    try {
      await onSyncNow();
    } catch (error) {
      console.error('[SyncStatusHeader] Sync Now failed:', error);
      // On error, close dropdown after brief delay
      setTimeout(() => {
        setIsDropdownOpen(false);
        syncInProgressRef.current = false;
      }, 1500);
    }
  }, [onSyncNow]);

  /**
   * Handle dropdown open/close
   * Prevents closing while sync is in progress
   */
  const handleDropdownOpenChange = useCallback((open: boolean) => {
    // Allow opening always
    if (open) {
      setIsDropdownOpen(true);
      return;
    }
    // Prevent closing while sync is in progress
    if (syncInProgressRef.current && syncStatus === 'syncing') {
      return; // Don't close
    }
    setIsDropdownOpen(false);
  }, [syncStatus]);

  /**
   * Format relative time from ISO string
   * Returns human-readable string like "2 min ago", "1 hour ago", "Yesterday"
   */
  const formatRelativeTime = useCallback((isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffDay === 1) return 'Yesterday';
    if (diffDay < 7) return `${diffDay} days ago`;
    return date.toLocaleDateString();
  }, []);

  /**
   * Determine if Sync Now should be disabled
   * Disabled when: syncing, connecting, remoteConnecting, offline, rateLimited, schemaVersionError
   */
  const isSyncNowDisabled = useMemo(() => {
    // Disabled if schema version error (sync is blocked until resolved)
    if (schemaVersionError) return true;
    // Disabled if currently syncing
    if (syncStatus === 'syncing') return true;
    // Disabled based on connection state
    if (!state) return true;
    return ['connecting', 'remoteConnecting', 'offline', 'rateLimited'].includes(state.status);
  }, [schemaVersionError, syncStatus, state]);

  // Don't render if no context available
  if (!connectionContext || !state) {
    return null;
  }

  const { Icon, dotColor, chipColor, label, iconAnimate } = statusConfig;

  // Map dot color to Tailwind background classes
  const dotColorClasses: Record<typeof dotColor, string> = {
    default: 'bg-default-400',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
  };

  // Map chipColor to Tailwind text classes for desktop label
  const labelColorClasses: Record<NonNullable<typeof chipColor>, string> = {
    default: 'text-default-600',
    primary: 'text-primary',
    secondary: 'text-secondary',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  };

  // Determine which action to show based on state
  const renderActionItems = () => {
    switch (state.status) {
      case 'idle':
        return (
          <DropdownItem
            key="connect"
            startContent={<LinkIcon className="h-4 w-4" />}
            onPress={handleConnect}
          >
            Connect
          </DropdownItem>
        );

      case 'connecting':
        return (
          <DropdownItem
            key="cancel"
            startContent={<X className="h-4 w-4" />}
            onPress={handleCancel}
            color="danger"
          >
            Cancel
          </DropdownItem>
        );

      case 'remoteConnecting':
        return (
          <DropdownItem
            key="remote-info"
            isReadOnly
            className="text-default-500 text-sm italic"
          >
            Another tab is connecting...
          </DropdownItem>
        );

      case 'success':
        return (
          <DropdownItem
            key="disconnect"
            startContent={<Unlink className="h-4 w-4" />}
            onPress={handleDisconnect}
            color="danger"
          >
            Disconnect
          </DropdownItem>
        );

      case 'needsReauth':
        return (
          <DropdownItem
            key="reconnect"
            startContent={<RefreshCw className="h-4 w-4" />}
            onPress={handleConnect}
            color="warning"
          >
            Reconnect
          </DropdownItem>
        );

      case 'offline':
        return (
          <DropdownItem
            key="offline-info"
            isReadOnly
            className="text-default-500 text-sm italic"
          >
            You&apos;re offline
          </DropdownItem>
        );

      case 'rateLimited':
        // Optional enhancement: Add countdown timer using state.retryAfterMs
        return (
          <DropdownItem
            key="rate-limited"
            isReadOnly
            className="text-default-500 text-sm italic"
          >
            Rate limited - please wait
          </DropdownItem>
        );

      case 'error':
        return (
          <DropdownItem
            key="retry"
            startContent={<RefreshCw className="h-4 w-4" />}
            onPress={handleConnect}
            color="warning"
          >
            Reconnect
          </DropdownItem>
        );

      default:
        return null;
    }
  };

  /**
   * Render schema version error content for dropdown
   * Shows contextual message based on error code
   */
  const renderSchemaErrorContent = () => {
    if (!schemaVersionError) return null;

    if (schemaVersionError.code === 'SCHEMA_VERSION_NEWER') {
      // User's browser has older app version - needs refresh
      return (
        <DropdownItem
          key="schema-newer"
          isReadOnly
          startContent={<AlertTriangle className="h-4 w-4 text-warning" />}
          className="text-sm"
          description="Your browser has an older version. Refresh to continue syncing."
        >
          App Update Available
        </DropdownItem>
      );
    }

    // MISSING, INVALID, MISMATCH - corrupted cloud data
    return (
      <DropdownItem
        key="schema-corrupted"
        isReadOnly
        startContent={<AlertCircle className="h-4 w-4 text-danger" />}
        className="text-sm"
        description="Visit Data & Sync settings to resolve."
      >
        Cloud Data Issue
      </DropdownItem>
    );
  };

  // Show settings link for main variant only
  const showSettings = variant === 'main' && showSettingsLink;

  return (
    <Dropdown
      placement="bottom-end"
      isOpen={isDropdownOpen}
      onOpenChange={handleDropdownOpenChange}
      closeOnSelect={false}
      {...(className && { className })}
    >
      <DropdownTrigger>
        <Button
          variant="light"
          size="sm"
          className="min-h-[44px] min-w-[44px] gap-1 px-2"
          aria-label={`Sync status: ${label} - ${providerDisplayName}${syncStatus === 'syncing' ? ' (syncing)' : ''}`}
        >
          {/* Icon - override with spinning RefreshCw when syncing */}
          {syncStatus === 'syncing' ? (
            <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Icon
              className={`h-4 w-4 ${iconAnimate ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          )}
          {/* Status dot - visible on all screen sizes */}
          <span
            className={`h-2 w-2 rounded-full ${syncStatus === 'syncing' ? 'bg-primary animate-pulse' : dotColorClasses[dotColor]}`}
            aria-hidden="true"
          />
          {/* Desktop: Status label */}
          <span
            className={`hidden md:inline text-sm font-medium ${syncStatus === 'syncing' ? 'text-primary' : labelColorClasses[chipColor]}`}
            aria-hidden="true"
          >
            {syncStatus === 'syncing' ? 'Syncing...' : label}
          </span>
          {/* Dropdown arrow */}
          <ChevronDown className="h-3 w-3 text-default-500" aria-hidden="true" />
        </Button>
      </DropdownTrigger>

      <DropdownMenu aria-label="Sync status and actions">
        {/* Status header section */}
        <DropdownSection title={`${label} • ${providerDisplayName}`} showDivider={showSettings || !!onSyncNow}>
          {schemaVersionError ? (
            renderSchemaErrorContent()
          ) : popupBlocked ? (
            <DropdownItem
              key="popup-blocked"
              isReadOnly
              startContent={<AlertTriangle className="h-4 w-4 text-warning" />}
              className="text-warning text-sm"
            >
              Please enable popups for this site
            </DropdownItem>
          ) : (
            renderActionItems()
          )}
        </DropdownSection>

        {/* Sync Now + Last Sync (main variant only, when onSyncNow provided) */}
        {onSyncNow ? (
          <DropdownSection showDivider={showSettings}>
            <DropdownItem
              key="sync-now"
              startContent={
                syncStatus === 'syncing' ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : syncStatus === 'success' ? (
                  <CheckCircle className="h-4 w-4 text-success" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )
              }
              onPress={handleSyncNow}
              isDisabled={isSyncNowDisabled}
              description={lastSyncAt ? `Last sync: ${formatRelativeTime(lastSyncAt)}` : undefined}
            >
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
            </DropdownItem>
          </DropdownSection>
        ) : null}

        {/* Settings link (main variant only) */}
        {showSettings ? (
          <DropdownSection>
            <DropdownItem
              key="settings"
              startContent={<Settings className="h-4 w-4" />}
              onPress={handleSettingsClick}
            >
              Settings
            </DropdownItem>
          </DropdownSection>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
}

/**
 * Memoized SyncStatusHeader component
 */
export const SyncStatusHeader = memo(SyncStatusHeaderComponent);
SyncStatusHeader.displayName = 'SyncStatusHeader';
